import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import Swal from 'sweetalert2';
import PageHeader from '../../components/PageHeader';

const LMS = process.env.REACT_APP_API_LMS;
const LMS_TOKEN = process.env.REACT_APP_API_TOKEN;

// Custom styles for react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#0d6efd' : base.borderColor,
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : base.boxShadow,
    '&:hover': {
      borderColor: '#0d6efd'
    }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : base.backgroundColor,
    color: state.isSelected ? '#fff' : base.color,
    '&:active': {
      backgroundColor: '#0d6efd'
    }
  })
};

const DAYS_OF_WEEK = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
  { value: 'Saturday', label: 'Saturday' },
  { value: 'Sunday', label: 'Sunday' },
];

const POSTPONE_OPTIONS = [
  { value: 0, label: 'No - Schedule as planned' },
  { value: 1, label: 'Yes - Mark as postponed' }
];

const IFAddScheduleForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const scheduleId = queryParams.get('schedule_id');
  const batchId = queryParams.get('batch');
  const programId = queryParams.get('program');
  const isEdit = Boolean(scheduleId);

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [courses, setCourses] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [meetLinkLoading, setMeetLinkLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      course: null,
      date: '',
      day: null,
      startTime: '',
      endTime: '',
      meetingUrl: '',
      postponeStatus: { value: 0, label: 'No - Schedule as planned' },
      remark: '',
    },
  });

  const watchCourse = watch('course');
  const watchDate = watch('date');
  const watchDay = watch('day');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');
  const watchPostponeStatus = watch('postponeStatus');

  // Auto-populate day when date is selected
  useEffect(() => {
    if (watchDate) {
      const selectedDate = new Date(watchDate);
      // Add one day to handle timezone offset issues
      selectedDate.setMinutes(selectedDate.getMinutes() + selectedDate.getTimezoneOffset());
      const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });
      const dayOption = DAYS_OF_WEEK.find(d => d.value === dayName);
      if (dayOption) {
        setValue('day', dayOption);
      }
    }
  }, [watchDate, setValue]);

  // Fetch Courses by Program
  useEffect(() => {
    if (!programId) return;

    const fetchCourses = async () => {
      try {
        const response = await fetch(`${LMS}/api/courses-by-program`, {
          method: 'POST',
          headers: {
            'Authorization': LMS_TOKEN,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ program_ids: [parseInt(programId, 10)] }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch courses');
        }
        
        const data = await response.json();
        setCourses(data.data || []);
      } catch (error) {
        console.error('Error fetching courses:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to load courses',
        });
      }
    };

    fetchCourses();
  }, [programId]);

  // Fetch Batches - Only to get batch name for display
  useEffect(() => {
    if (!batchId) return;

    const fetchBatchName = async () => {
      try {
        const response = await fetch(`${LMS}/api/batch/${batchId}`, {
          method: 'GET',
          headers: {
            'Authorization': LMS_TOKEN,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch batch details');
        }
        
        const data = await response.json();
        setBatchName(data.data?.batch_name || `Batch #${batchId}`);
      } catch (error) {
        console.error('Error fetching batch:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to load batch details',
        });
        setBatchName(`Batch #${batchId}`);
      } finally {
        setDataLoading(false);
      }
    };

    fetchBatchName();
  }, [batchId]);

  // Fetch Schedule Details for Edit
  useEffect(() => {
    if (!isEdit || !scheduleId || courses.length === 0) {
      if (!isEdit && batchName) setDataLoading(false);
      return;
    }

    const fetchScheduleDetails = async () => {
      setDataLoading(true);
      try {
        const response = await fetch(`${LMS}/api/batch-schedule/${scheduleId}`, {
          method: 'GET',
          headers: {
            'Authorization': LMS_TOKEN,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch schedule details');
        }
        
        const result = await response.json();
        const schedule = result.data;

        console.log('Schedule data:', schedule);

        // Populate form with schedule data
        const selectedCourse = courses.find(c => c.id === schedule.course_id);
        const selectedDay = DAYS_OF_WEEK.find(d => d.value === schedule.day);
        const selectedPostpone = POSTPONE_OPTIONS.find(p => p.value === schedule.postpone_status);

        if (selectedCourse) {
          setValue('course', { 
            value: selectedCourse.id, 
            label: selectedCourse.title 
          });
        }

        setValue('date', schedule.date);
        
        if (selectedDay) {
          setValue('day', selectedDay);
        }

        setValue('startTime', schedule.start_time?.substring(0, 5) || schedule.start_time);
        setValue('endTime', schedule.end_time?.substring(0, 5) || schedule.end_time);
        setValue('meetingUrl', schedule.meet_url || '');
        
        if (selectedPostpone) {
          setValue('postponeStatus', selectedPostpone);
        }
        
        setValue('remark', schedule.remark || '');
        
      } catch (error) {
        console.error('Error fetching schedule details:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to load schedule details',
        });
      } finally {
        setDataLoading(false);
      }
    };

    fetchScheduleDetails();
  }, [isEdit, scheduleId, setValue, courses]);

  const fetchMeetLink = async () => {
    if (!batchId) return '';
    try {
      setMeetLinkLoading(true);
      const res = await fetch(`${LMS}/api/zoom-link?batch_id=${parseInt(batchId, 10)}`, {
        method: 'GET',
        headers: {
          'Authorization': LMS_TOKEN,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const json = await res.json().catch(() => ({}));
      const link = json?.link || json?.data?.link || '';
      if (link) {
        setValue('meetingUrl', link, { shouldValidate: true });
      }
      return link;
    } catch (err) {
      console.error('Meet link fetch failed', err);
      return '';
    } finally {
      setMeetLinkLoading(false);
    }
  };

  // Convert to react-select format
  const courseOptions = courses.map(c => ({
    value: c.id,
    label: c.title
  }));

  const onSubmit = async (data) => {
    if (!batchId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Batch ID is required. Please access this form from the batch schedule page.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let ensuredLink = (data.meetingUrl || '').trim();
      if (!ensuredLink) {
        ensuredLink = await fetchMeetLink();
      }
      const payload = {
        batch_id: parseInt(batchId),
        course_id: parseInt(data.course?.value),
        date: data.date,
        day: data.day?.value,
        start_time: data.startTime,
        end_time: data.endTime,
        postpone_status: data.postponeStatus?.value,
        meet_url: ensuredLink || data.meetingUrl,
        remark: data.remark || '',
      };

      const url = isEdit 
        ? `${LMS}/api/batch-schedule/update/${scheduleId}` 
        : `${LMS}/api/batch-schedule/store`;
      
      const method = isEdit ? 'POST' : 'POST';

      console.log('Payload:', payload);

      const response = await fetch(url, {
        method: method,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': LMS_TOKEN,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to save schedule');
      }

      console.log('Schedule saved:', result);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: result.message || `Schedule ${isEdit ? 'updated' : 'created'} successfully!`,
        timer: 1200,
        showConfirmButton: false,
      });
      
      setTimeout(() => {
        if (batchId && programId) {
          navigate(`/teacher-view-schedule?batch=${batchId}&program=${programId}`);
        } else {
          navigate('/teacher-view-schedule');
        }
      }, 1200);
    } catch (error) {
      console.error('Error saving schedule:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save schedule. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    reset();
    setMessage({ type: '', text: '' });
  };

  const calculateDuration = () => {
    if (watchStartTime && watchEndTime) {
      const startTime = new Date(`2000-01-01T${watchStartTime}`);
      const endTime = new Date(`2000-01-01T${watchEndTime}`);
      const durationMs = endTime - startTime;
      const durationHours = Math.round((durationMs / (1000 * 60 * 60)) * 10) / 10;
      return durationHours > 0 ? `${durationHours} hours` : '';
    }
    return '';
  };

  const todayStr = new Date().toISOString().split('T')[0];

  if (dataLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Schedule' : 'Add New Schedule'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/teacher-dashboard' },
          { label: 'Batches', path: '/teacher-batches' },
          { label: 'Schedule', path: `/teacher-view-schedule?batch=${batchId}&program=${programId}` },
          { label: isEdit ? 'Edit' : 'Add', active: true },
        ]}
      />

      {message.text && (
        <Alert 
          variant={message.type} 
          dismissible 
          onClose={() => setMessage({ type: '', text: '' })} 
          className={message.type === 'success' ? 'text-white' : ''}
        >
          {message.text}
        </Alert>
      )}

      <Card className="skyblueborder">
        <Card.Header className="skyblue-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
          <h5 className="mb-0 text-primary">{isEdit ? 'Update Schedule Details' : 'Enter Schedule Information'}</h5>
          <Button
            variant="link"
            className="p-0 text-decoration-none text-primary fw-semibold"
            onClick={() => navigate('/teacher-batches')}
          >
            <i className="fas fa-arrow-left me-1"></i> Back to Batch List
          </Button>
        </Card.Header>
        <Card.Body>
          <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            {/* Batch Info Display - Read Only */}
            <Row className="mb-3">
              <Col md={12}>
                <div className="alert alert-info d-flex align-items-center">
                  <i className="fas fa-info-circle me-2"></i>
                  <div>
                    <strong>Batch:</strong> {batchName || `Batch #${batchId}`}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Course <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="course"
                    control={control}
                    rules={{ required: 'Course is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={courseOptions}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.course ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.course ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                        placeholder="Choose course..."
                        isClearable
                        className={errors.course ? 'is-invalid' : ''}
                      />
                    )}
                  />
                  {errors.course && (
                    <div className="invalid-feedback d-block">{errors.course?.message}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
              <Form.Group>
                <Form.Label>Meeting URL</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="text"
                    value={watch('meetingUrl') || ''}
                    disabled
                    placeholder="Click Get Link to fetch"
                    className="bg-light"
                  />
                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={fetchMeetLink}
                    disabled={meetLinkLoading || !batchId}
                  >
                    {meetLinkLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Getting...
                      </>
                    ) : (
                      'Get Link'
                    )}
                  </Button>
                </div>
                <Form.Text muted>Meeting link is fetched and inserted automatically</Form.Text>
              </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    {...register('date', { required: 'Date is required' })}
                    isInvalid={errors.date}
                    min={todayStr}
                    onFocus={(e) => e.target.showPicker()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.date?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Day <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="day"
                    control={control}
                    rules={{ required: 'Day is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={DAYS_OF_WEEK}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.day ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.day ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                        placeholder="Select day..."
                        isClearable
                        className={errors.day ? 'is-invalid' : ''}
                      />
                    )}
                  />
                  {errors.day && (
                    <div className="invalid-feedback d-block">{errors.day?.message}</div>
                  )}
                  <Form.Text muted>Auto-populated from date</Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Postpone Status</Form.Label>
                  <Controller
                    name="postponeStatus"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={POSTPONE_OPTIONS}
                        styles={customSelectStyles}
                        placeholder="Select status..."
                      />
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Start Time <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="time"
                    {...register('startTime', { required: 'Start time is required' })}
                    isInvalid={errors.startTime}
                    onFocus={(e) => e.target.showPicker()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.startTime?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>End Time <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="time"
                    {...register('endTime', {
                      required: 'End time is required',
                      validate: (val) => {
                        if (!watchStartTime) return true;
                        const start = new Date(`2000-01-01T${watchStartTime}`);
                        const end = new Date(`2000-01-01T${val}`);
                        return end > start ? true : 'End time must be after start time';
                      }
                    })}
                    isInvalid={errors.endTime}
                    onFocus={(e) => e.target.showPicker()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.endTime?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={calculateDuration()}
                    disabled
                    placeholder="Auto-calculated"
                    className="bg-light"
                  />
                  <Form.Text muted>Automatically calculated</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label>Remark</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    {...register('remark', {
                      maxLength: { value: 500, message: 'Max 500 characters' }
                    })} 
                    isInvalid={errors.remark}
                    placeholder="Enter any additional remarks, notes, or special instructions..."
                  />
                  <Form.Text muted>{watch('remark')?.length || 0}/500</Form.Text>
                  <Form.Control.Feedback type="invalid">{errors.remark?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Summary Card */}
            {(watchCourse || watchDate) && (
              <Row className="mb-4">
                <Col>
                  <Card className="bg-light border-primary">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0 text-white">📅 Schedule Summary</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1">
                            <strong>Batch:</strong> {batchName || `Batch #${batchId}`}
                          </p>
                          <p className="mb-1">
                            <strong>Course:</strong> {watchCourse?.label || 'Not selected'}
                          </p>
                          <p className="mb-0">
                            <strong>Date:</strong> {watchDate ? `${watchDate} (${watchDay?.label || ''})` : 'Not set'}
                          </p>
                        </Col>
                        <Col md={6}>
                          <p className="mb-1">
                            <strong>Time:</strong> {watchStartTime && watchEndTime ? `${watchStartTime} - ${watchEndTime}` : 'Not set'}
                          </p>
                          <p className="mb-1">
                            <strong>Duration:</strong> {calculateDuration() || 'Not calculated'}
                          </p>
                          <p className="mb-0">
                            <strong>Status:</strong>{' '}
                            {watchPostponeStatus?.value === 1 ? (
                              <span className="text-warning">Postponed</span>
                            ) : (
                              <span className="text-success">Scheduled</span>
                            )}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Action Buttons */}
            <div className="d-flex gap-2 flex-wrap">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-1" /> {isEdit ? 'Update Schedule' : 'Create Schedule'}
                  </>
                )}
              </Button>

              <Button type="button" variant="outline-secondary" onClick={onReset} disabled={loading}>
                <i className="fas fa-sync-alt me-1" /> Reset
              </Button>

              <Button
                type="button"
                variant="outline-danger"
                onClick={() => {
                  Swal.fire({
                    title: 'Are you sure?',
                    text: 'Cancel? Unsaved changes will be lost.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Yes, cancel',
                    cancelButtonText: 'No, stay'
                  }).then((result) => {
                    if (result.isConfirmed) {
                      if (batchId && programId) {
                        navigate(`/teacher-view-schedule?batch=${batchId}&program=${programId}`);
                      } else {
                        navigate('/teacher-view-schedule');
                      }
                    }
                  });
                }}
                disabled={loading}
              >
                <i className="fas fa-times me-1" /> Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default IFAddScheduleForm;
